const API_BASE = 'https://eurocentrumv1.onrender.com';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function register(username, password) {
  const res = await fetch(`${API_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return res.json();
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return res.json();
}

export async function getListings(search = '') {
  const url = search
    ? `${API_BASE}/listings?search=${encodeURIComponent(search)}`
    : `${API_BASE}/listings`;
  const res = await fetch(url);
  return res.json();
}

export async function getListingDetail(_id) {
  const res = await fetch(`${API_BASE}/listings/${_id}`);
  return res.json();
}

export async function createListing(data) {
  const res = await fetch(`${API_BASE}/listings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateListing(_id, data) {
  const res = await fetch(`${API_BASE}/listings/${_id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteListing(_id) {
  const res = await fetch(`${API_BASE}/listings/${_id}`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeaders(),
    },
  });
  return res.json();
}

/**
 * Refresh the signed URLs for images in a listing
 * @param {string} listingId - ID of the listing to refresh image URLs for
 * @returns {Promise<Object>} - Updated listing with fresh image URLs
 */
export async function refreshImageUrls(listingId) {
  try {
    console.log(`API: Refreshing image URLs for listing ${listingId}`);
    const response = await fetch(`${API_BASE}/renew-image-urls/${listingId}`, {
      headers: {
        ...getAuthHeaders()
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to refresh image URLs: HTTP ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error refreshing image URLs for listing ${listingId}:`, error);
    throw error;
  }
}

/**
 * Check if a listing's images need URL refresh
 * @param {Object} listing - The listing object to check
 * @returns {boolean} - True if any image needs refresh, false otherwise
 */
export function checkNeedsUrlRefresh(listing) {
  // If no images, no need to refresh
  if (!listing.images || listing.images.length === 0) {
    return false;
  }
  
  return listing.images.some(img => {
    // If no fileUrl, it needs refresh
    if (!img.fileUrl) return true;
    
    // If URL contains a timestamp, extract it
    try {
      if (img.fileUrl && img.fileUrl.includes('Expires=')) {
        const expiryMatch = img.fileUrl.match(/Expires=(\d+)/);
        if (expiryMatch && expiryMatch[1]) {
          const expiryTimestamp = parseInt(expiryMatch[1], 10) * 1000; // Convert to milliseconds
          const now = Date.now();
          const sixDaysMs = 6 * 24 * 60 * 60 * 1000;
          
          // If URL expires in less than 6 days, refresh
          return (expiryTimestamp - now) < sixDaysMs;
        }
      }
    } catch (err) {
      console.error("Error parsing URL expiry:", err);
    }
    
    return false;
  });
}

/**
 * Upload images to the server, ensuring they're stored in Google Cloud Storage
 * @param {File[]} images - Array of image files to upload
 * @returns {Promise<Array>} - Array of uploaded image objects with URLs
 */
export async function uploadImages(imageFiles) {
  const formData = new FormData();
  
  // Add each image to the form data
  imageFiles.forEach(file => {
    formData.append('images', file);
  });
  
  // Upload to the server
  const res = await fetch(`${API_BASE}/uploads`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders()
    },
    body: formData
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.error || 'Failed to upload images');
  }
  
  return res.json();
}

// About Us Images API functions
export async function getAboutImages(section) {
  const res = await fetch(`${API_BASE}/api/about-images/${section}`, {
    headers: getAuthHeaders()
  });
  
  if (!res.ok) {
    throw new Error(`Failed to fetch ${section} images`);
  }
  
  return res.json();
}

// Debug functions for testing deployment
export async function testDeployment() {
  try {
    console.log('🔧 Testing deployment status...');
    const response = await fetch(`${API_BASE}/api/deployment-test`);
    const data = await response.json();
    console.log('🔧 Deployment test result:', data);
    return data;
  } catch (error) {
    console.error('🔧 Deployment test failed:', error);
    throw error;
  }
}

export async function getDebugStatus() {
  try {
    console.log('🐛 Getting debug status...');
    const response = await fetch(`${API_BASE}/api/debug-status`);
    const data = await response.json();
    console.log('🐛 Debug status result:', data);
    return data;
  } catch (error) {
    console.error('🐛 Debug status failed:', error);
    throw error;
  }
}

export async function uploadAboutImages(section, imageFiles) {
  const formData = new FormData();
  
  imageFiles.forEach(file => {
    formData.append('images', file);
  });

  console.log(`🚀 API: Uploading to ${API_BASE}/api/about-images/${section}/upload`);
  console.log(`📁 API: Files:`, imageFiles.map(f => f.name));
  
  const res = await fetch(`${API_BASE}/api/about-images/${section}/upload`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: formData
  });
  
  console.log(`📡 API: Response status: ${res.status}`);
  console.log(`📡 API: Response headers:`, Object.fromEntries(res.headers.entries()));
  
  if (!res.ok) {
    const errorText = await res.text();
    console.error(`❌ API: Error response (${res.status}):`, errorText);
    
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch (e) {
      // If not JSON, create error object with the text
      errorData = { error: errorText.substring(0, 200) + (errorText.length > 200 ? '...' : '') };
    }
    
    throw new Error(errorData.error || `HTTP ${res.status}: ${errorText}`);
  }
  
  const result = await res.json();
  console.log(`✅ API: Upload successful:`, result);
  return result;
}

export async function setCurrentAboutImage(section, imageIndex) {
  const res = await fetch(`${API_BASE}/api/about-images/${section}/current/${imageIndex}`, {
    method: 'PUT',
    headers: getAuthHeaders()
  });
  
  if (!res.ok) {
    throw new Error('Failed to update current image');
  }
  
  return res.json();
}

export async function deleteAboutImage(section, imageIndex) {
  const res = await fetch(`${API_BASE}/api/about-images/${section}/delete/${imageIndex}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.error || 'Failed to delete image');
  }
  
  return res.json();
}

// About Us Text Content API functions
export async function getAboutText() {
  const res = await fetch(`${API_BASE}/api/about-text`);
  
  if (!res.ok) {
    throw new Error('Failed to fetch about text content');
  }
  
  return res.json();
}

export async function updateAboutText(section, text) {
  const res = await fetch(`${API_BASE}/api/about-text`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ section, text })
  });
  
  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.error || 'Failed to update text content');
  }
  
  return res.json();
}



